/**
 * Coalesces concurrent async work for the same key while allowing a new key
 * to start immediately. The reference identity check in `finally` prevents a
 * slower previous operation from clearing a newer one.
 */
export function createSingleFlight<T>() {
  let inFlight: { key: string; promise: Promise<T> } | null = null;

  return {
    run(key: string, operation: () => Promise<T>): Promise<T> {
      if (inFlight?.key === key) return inFlight.promise;

      let operationResult: Promise<T>;
      try {
        operationResult = Promise.resolve(operation());
      } catch (error) {
        operationResult = Promise.reject(error);
      }
      const promise = operationResult.finally(() => {
        if (inFlight?.promise === promise) inFlight = null;
      });
      inFlight = { key, promise };
      return promise;
    },
    clear(): void {
      inFlight = null;
    },
  };
}
